import React, { useMemo, useState } from 'react';
import { useGetPage } from 'hooks/useGetPage';
import {
  LOCAL_STORAGE_SHOP_SELECTED_PROVIDER,
  pageIds,
  PARAM_MODALS_IDENTIFIERS,
} from 'utilities/constants';
import Loader from 'components/loaderComponent';
import Alert from 'components/alertComponent';
import {
  ProductOutput,
  useGetProvidersByPatientIdQuery,
  useGetShopProductsQuery,
} from 'graphql/generated/remote-schema-hasura';
import { ReactComponent as Cart } from 'assets/icons/shop/shop.svg';
import ButtonComponent from 'components/button/buttonComponent';
import { ORDERS, PROVIDERS, TEST_KITS_MANAGEMENT } from 'utilities/routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { Option } from 'react-multi-select-component';
import StorageService from 'utilities/storageService';
import { Recommendations } from './components/Recommendations';
import { useAuth } from 'auth/context/AuthContext';
import TestBlock from './components/Tests/TestBlock';
import useShopCart from 'hooks/useShopCart';
import ProductDetailsModal from './components/Tests/ProductDetailsModal';
import CartModal from './components/Tests/CartModal';
import PaginateBlock from '../../components/PaginateBlock';
import { getRootConfig } from '../../utilities/config';

export enum ProductCategory {
  TestKit = 1,
  ShipMonkSupplement = 2,
}

const TABS: ProductCategory[] = [
  ProductCategory.TestKit,
  ProductCategory.ShipMonkSupplement,
];

const DEFAULT_VARIABLES = {
  limit: 16,
  offset: 0,
  categories: [ProductCategory.TestKit],
};

const Shop = () => {
  const { user: loggedUser } = useAuth();
  const { data: locale, loading: isLocaleLoading } = useGetPage({
    locale: 'en',
    pageId: pageIds.SHOP_DIAGNOSTIC,
  });

  const [variables, setVariables] = useState(DEFAULT_VARIABLES);
  const [, setSearchParams] = useSearchParams();

  const { data, loading } = useGetShopProductsQuery({
    variables: variables,
  });

  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<ProductOutput | null>(
    null,
  );
  const selectedProviderStorage = new StorageService(
    LOCAL_STORAGE_SHOP_SELECTED_PROVIDER,
    localStorage,
    '',
  );

  const [myProviders, updateMyProviders] = useState<Array<Option>>([]);
  const [isLoadingCartMutation, updateIsLoadingCartMutation] = useState(false);
  const [isOpenCart, setIsOpenCart] = useState(false);

  const hasProvider = !!myProviders.length;

  const handleOpenArchiveModal = () => {
    navigate(
      `.?${PARAM_MODALS_IDENTIFIERS.ARCHIVE_RECOMMENDATIONS_MODAL}=true`,
    );
  };

  const goToSelectProvider = () => {
    navigate(PROVIDERS, {
      state: {
        isFromShoppingCart: true,
      },
    });
  };

  useGetProvidersByPatientIdQuery({
    fetchPolicy: 'network-only',
    variables: {
      onlyAvailable: true,
      patientCodexId: loggedUser?.uuid,
    },
    onError: (error) => {
      console.error('Error getting providers', error);
      return;
    },
    onCompleted: (data) => {
      const providersList = data.getProvidersByPatientId?.providers.map(
        ({ id, SENSITIVE_firstname, SENSITIVE_lastname }) => ({
          label: `${SENSITIVE_firstname} ${SENSITIVE_lastname}`,
          value: id,
        }),
      );
      updateMyProviders(providersList);
      const savedSelectedProvider = selectedProviderStorage.getData();
      if (!savedSelectedProvider) {
        selectedProviderStorage.setData(providersList[0]);
        return;
      }
    },
  });

  const {
    totalItems,
    totalPrice,
    isLoadingCartData,
    updateSelectedProducts,
    removeCartProductByProductId,
    isLoadingRequests,
    cartProducts,
    addRecommendationToCart,
    currentSelectedProducts,
  } = useShopCart(() => updateIsLoadingCartMutation(false));

  const setNewOffset = (newOffset: number) => {
    setVariables({ ...variables, offset: newOffset });
  };

  const setNewCategory = (category: ProductCategory) => {
    setSearchParams((prev) => ({
      ...prev,
      offset: '0',
    }));
    setVariables({ ...variables, offset: 0, categories: [Number(category)] });
  };

  const hasShowPaginate = useMemo(
    () =>
      !!data?.getProducts?.total && data?.getProducts?.total > variables.limit,
    [data?.getProducts?.total, variables.limit],
  );

  const { environment } = getRootConfig();

  const showTabs = environment === 'development';

  if (!locale || isLocaleLoading) return <Loader />;

  return (
    <div className="desktop:px-8 flex flex-col gap-4 mt-10 desktop:mt-0">
      <div className="flex flex-col desktop:flex-row items-start gap-5 desktop:items-center mb-8">
        <h1 className="text-h1 text-neutral-800 font-medium mr-auto">
          {locale?.title}
        </h1>
        {hasProvider && (
          <>
            {!isLoadingCartData && (
              <span className="text-neutral-800 font-semibold text-h4">
                Total: ${totalPrice}
              </span>
            )}
            <ButtonComponent
              iconPosition="left"
              Icon={Cart}
              testID="chart"
              className="group"
              onClick={() => setIsOpenCart(true)}
              disabled={isLoadingCartData}
              showSpinner={isLoadingCartData}
            >
              <span className="text-center">Cart</span>
              <span
                className={twMerge(
                  'group-hover:text-white group-hover:bg-clc-blue p-1 max-h-[24px] flex items-center justify-center rounded-full bg-white text-clc-blue font-bold text-sm',
                  !totalPrice && 'hidden',
                )}
              >
                {totalItems.toString().padStart(2, '0')}
              </span>
            </ButtonComponent>
          </>
        )}
      </div>

      {hasProvider && <Recommendations />}

      {!hasProvider && (
        <Alert
          desktopItemsAlign="!items-start desktop:!items-center border border-amber-border"
          textClassName="text-amber-border text-left font-medium"
          iconClassName="fill-amber-border h-8 w-8"
          type="info"
          customContent={
            <div className="w-full flex-col desktop:flex-row gap-4 flex items-center justify-between">
              <p className="text-amber-border font-semibold">
                {locale?.alertText}
              </p>
              <ButtonComponent
                testID="find_provider"
                className="!w-full desktop:!w-fit !text-sm whitespace-nowrap !px-5"
                onClick={goToSelectProvider}
              >
                {locale?.findAProvider}
              </ButtonComponent>
            </div>
          }
        />
      )}

      {showTabs && (
        <nav className="flex flex-row items-center w-full bg-white rounded-t-10 mt-8">
          {TABS.map((tab) => {
            const isActive = variables.categories.includes(tab);

            return (
              <button
                key={tab}
                type="button"
                data-testid={`tab-${tab}`}
                onClick={() => setNewCategory(tab)}
                className={twMerge(
                  'w-min whitespace-nowrap text-center py-[18px] px-30 border-2 border-transparent justify-center text-med-gray-3 text-sm font-semibold leading-tight hover:text-clc-blue',
                  isActive && 'text-clc-blue border-b-clc-blue',
                )}
              >
                {locale?.tabs?.[tab] || tab}
              </button>
            );
          })}
        </nav>
      )}

      <div
        className={twMerge(
          'rounded-b-10 bg-white px-0 desktop:px-14 pt-5 desktop:py-8 flex flex-col ',
          'gap-0 desktop:gap-8 h-fit',
          hasProvider ? 'pb-5' : 'pb-0',
        )}
      >
        <div className="w-full px-5 desktop:px-0 flex items-center justify-between border-b pb-2.5">
          <div>
            <label className="text-xl font-semibold">
              {locale?.tableTitle}
            </label>

            {hasProvider && (
              <>
                <p className="mt-2.5 text-granite-grey font-medium hidden desktop:block">
                  {locale?.tableSubTitle1}
                </p>
                <p className="text-granite-grey font-medium hidden desktop:block">
                  {locale?.tableSubTitle2}
                </p>
              </>
            )}
          </div>

          <ButtonComponent
            onClick={() => navigate(`${TEST_KITS_MANAGEMENT}${ORDERS}`)}
            className="ml-auto !h-fit w-full"
            type="outlined-transparent"
            testID="orders-history"
          >
            {'Orders History'}
          </ButtonComponent>
        </div>

        <div className="w-full">
          {loading ? (
            <div className="h-[963px] flex items-center justify-center">
              <Loader />
            </div>
          ) : (
            <div className="grid desktop:p-0 p-5 desktop:grid-cols-4 tablet:grid-cols-2 grid-cols-1 justify-center gap-5">
              {(data?.getProducts?.products ?? []).map((product) => (
                <TestBlock
                  isLoadingCartMutation={isLoadingCartMutation}
                  key={product.id}
                  hideButtons={!hasProvider}
                  created_at={''}
                  updated_at={''}
                  isLoadingCartData={isLoadingCartData}
                  hasSelectedProduct={
                    !!currentSelectedProducts?.includes(product.id)
                  }
                  addToCart={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    addRecommendationToCart(product.id, 1, null);
                  }}
                  removeFromCart={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    removeCartProductByProductId(product.id);
                  }}
                  viewDetail={() =>
                    setSelectedProduct(product as ProductOutput)
                  }
                  {...product}
                />
              ))}
            </div>
          )}

          {hasShowPaginate && (
            <div className="flex max-desktop:flex-col gap-2.5 justify-between items-center mt-5">
              <p className="font-medium text-sm text-med-gray-3" />
              <PaginateBlock
                key={variables.categories?.[0]}
                itemsPerPage={variables.limit}
                totalItems={data?.getProducts?.total || 0}
                setNewOffset={setNewOffset}
              />
            </div>
          )}
        </div>
      </div>

      <ButtonComponent
        type="underline"
        className="mr-auto mt-5"
        onClick={handleOpenArchiveModal}
        testID="archive"
      >
        View Recommendation Archive
      </ButtonComponent>

      {!!selectedProduct && (
        <ProductDetailsModal
          {...selectedProduct}
          isLoadingCartData={isLoadingCartData}
          onClose={() => setSelectedProduct(null)}
          hasSelectedProduct={
            !!currentSelectedProducts?.includes(selectedProduct.id)
          }
          addToCart={() => addRecommendationToCart(selectedProduct.id, 1, null)}
          removeFromCart={() =>
            removeCartProductByProductId(selectedProduct.id)
          }
        />
      )}

      <CartModal
        updateSelectedProducts={updateSelectedProducts}
        totalPrice={totalPrice}
        cartProducts={cartProducts}
        isLoadingCartData={isLoadingCartData}
        isLoadingCartMutation={isLoadingCartMutation}
        updateIsLoadingCartMutation={updateIsLoadingCartMutation}
        isOpen={isOpenCart}
        onClose={() => setIsOpenCart(false)}
        isLoadingRequests={isLoadingRequests}
      />
    </div>
  );
};

export default Shop;
